<script>
// Cabeçalho de uma "Parte" da sidebar de Gestão mais a região expansível com os
// módulos dela. Apenas apresentacional e controlado pelo pai: recebe `open`,
// `active` e os módulos, emite `toggle` no acionamento do cabeçalho.
//
// O que este componente NÃO faz: decidir qual Parte abre, conhecer a rota atual,
// navegar, guardar estado de abertura ou rolar a navegação. Tudo isso é do
// gestao-sidebar, que é o único orquestrador.
import NavItem from './nav-item.vue'
import { MATCH_BY } from './nav-flyout.vue'

// Prefixo do id da região. Determinístico pela chave da Parte (e não por índice do
// v-for) para o aria-controls sobreviver a reordenações e a Partes descartadas.
const REGION_ID_PREFIX = 'nav-parte-region-'
const CHEVRON_UP = 'mdi-chevron-up'
const CHEVRON_DOWN = 'mdi-chevron-down'
const ARIA_EXPANDED = { TRUE: 'true', FALSE: 'false' }
const ACTIVE_VARIANT = { FULL: 'full', SUBTLE: 'subtle' }

export default {
  name: 'NavParte',
  components: { NavItem },
  props: {
    parte: { type: Object, required: true },
    open: { type: Boolean, default: false },
    // A rota atual pertence a esta Parte. Independente de `open`.
    active: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    // Módulos da Parte. Repassados ao cabeçalho apenas no modo recolhido, onde
    // eles viram o conteúdo do painel flutuante.
    subitems: { type: Array, default: () => [] },
  },
  computed: {
    regionId() {
      return REGION_ID_PREFIX + this.parte.key
    },
    // O modo recolhido vence sobre `open`: nenhuma região expande em modo ícone.
    isOpen() {
      return !this.collapsed && this.open
    },
    headerSubitems() {
      return this.collapsed ? this.subitems : []
    },
    // Os módulos viajam dentro do item do cabeçalho porque o nav-item emite
    // hover-subitems com `item: this.item`, e o flyout lê label e subitems dali.
    headerItem() {
      return { ...this.parte, subitems: this.headerSubitems }
    },
    ariaExpanded() {
      return this.isOpen ? ARIA_EXPANDED.TRUE : ARIA_EXPANDED.FALSE
    },
    // Troca de ícone, não rotação por transform (precedente da nav antiga).
    chevronName() {
      return this.isOpen ? CHEVRON_UP : CHEVRON_DOWN
    },
    // Tri-estado do destaque: recolhido-ativo é sempre cheio, porque ali o
    // nav-item não desenha a barra lateral e o preenchimento é o único indicador
    // de posição. Expandido, o cheio marca a Parte fechada e o atenuado a aberta.
    activeVariant() {
      if (this.collapsed) return ACTIVE_VARIANT.FULL
      return this.open ? ACTIVE_VARIANT.SUBTLE : ACTIVE_VARIANT.FULL
    },
  },
  methods: {
    onToggle() {
      this.$emit('toggle', this.parte.key)
    },
    // O critério de destaque viaja no payload em vez de ser derivado de
    // `collapsed` no pai: o atalho Configurações também é um módulo com subitens
    // no rodapé recolhido, e o painel dele precisa continuar casando por rota.
    onHoverSubitems(payload) {
      this.$emit('hover-subitems', { ...payload, matchBy: MATCH_BY.MODULE })
    },
  },
}
</script>

<template>
  <div class="nav-parte">
    <NavItem
      :item="headerItem"
      :subitems="headerSubitems"
      :collapsed="collapsed"
      :mobile="mobile"
      :active="active"
      :active-variant="activeVariant"
      :chevron-name="chevronName"
      :expanded="ariaExpanded"
      :controls="regionId"
      :aria-label="parte.label"
      @click="onToggle"
      @hover-subitems="onHoverSubitems"
      @leave-subitems="$emit('leave-subitems')"
    />
    <v-expand-transition>
      <div v-show="isOpen" :id="regionId" class="nav-parte__region">
        <slot />
      </div>
    </v-expand-transition>
  </div>
</template>

<style lang="sass" scoped>
.nav-parte
  &__region
    // O recuo de 12px casa com o padding lateral do corpo da casca e cria a
    // segunda profundidade de barra que distingue módulo de Parte.
    padding: 4px 0 4px 12px
    display: flex
    flex-direction: column
    gap: 2px
</style>
